import { useCallback, useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';

import {
  GetMultiBuildingInspectionInstancesDocument,
  GetMultiBuildingInspectionInstancesQuery,
  InspectionInstanceStatus,
} from 'graphql-query-contracts';

const DEFAULT_PER_PAGE = 20;

export type UseGetMultiBuildingInspectionInstancesParams = {
  channelIds: string[];
  status: InspectionInstanceStatus;
  search?: string;
  perPage?: number;
  skip?: boolean;
};

type Instances = NonNullable<
  GetMultiBuildingInspectionInstancesQuery['multiBuildingInspectionInstances']
>['inspectionInstances'];

type PageInfo = {
  start: number;
  total: number;
  perPage: number;
};

const DEFAULT_PAGE_INFO: PageInfo = { start: 0, total: 0, perPage: DEFAULT_PER_PAGE };

export function useGetMultiBuildingInspectionInstances({
  channelIds,
  status,
  search,
  perPage = DEFAULT_PER_PAGE,
  skip = false,
}: UseGetMultiBuildingInspectionInstancesParams) {
  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const [
    fetchInstances,
    { data, previousData, loading, error, fetchMore, called, refetch: apolloRefetch },
  ] = useLazyQuery(GetMultiBuildingInspectionInstancesDocument, {
    fetchPolicy: 'network-only',
    client: getClient(),
  });

  const buildVariables = useCallback(
    (startPage: number) => ({
      channelIds,
      search: {
        status,
        ...(search ? { templateNameSearch: search } : {}),
      },
      pagination: {
        start: (startPage - 1) * perPage,
        perPage,
      },
    }),
    [channelIds, status, search, perPage]
  );

  // Fetch page 2+ — appends results to the existing list
  useEffect(() => {
    if (!called || page === 1) {
      return;
    }

    // Check if there's more data to fetch
    // Only fetch next page if current items count < total items
    // Example: total=25, perPage=20 → page 1 gets 20 items, page 2 should fetch remaining 5
    const currentData = data ?? previousData;
    const currentInstances = currentData?.multiBuildingInspectionInstances?.inspectionInstances ?? [];
    const currentPageInfo = currentData?.multiBuildingInspectionInstances?.pageInfo;

    const hasMoreData = currentPageInfo && currentInstances.length < currentPageInfo.total;

    if (!hasMoreData) {
      setIsFetchingMore(false);
      return;
    }

    setIsFetchingMore(true);
    fetchMore({
      variables: buildVariables(page),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevItems =
          prev.multiBuildingInspectionInstances.inspectionInstances;
        const newItems =
          fetchMoreResult.multiBuildingInspectionInstances.inspectionInstances;

        return {
          multiBuildingInspectionInstances: {
            __typename: 'InspectionInstanceResult' as const,
            pageInfo: {
              ...fetchMoreResult.multiBuildingInspectionInstances.pageInfo,
              total: prev.multiBuildingInspectionInstances.pageInfo.total,
            },
            inspectionInstances: [...prevItems, ...newItems],
          },
        };
      },
    })
      .catch((err: unknown) => {
        console.warn('useGetMultiBuildingInspectionInstances: fetchMore failed', err);
      })
      .finally(() => setIsFetchingMore(false));
  }, [page, buildVariables, called, fetchMore]);

  // Reset to page 1 and re-fetch when channelIds, status, or skip changes.
  // Suppressed while skip is true so AsyncStorage can hydrate first.
  useEffect(() => {
    if (skip) return;
    setPage(1);
    fetchInstances({ variables: buildVariables(1) });
  }, [skip, buildVariables, fetchInstances]);

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  // Use apolloRefetch (not re-calling fetchInstances) so Apollo keeps the previous
  // data visible while the new request is in flight — prevents the list from flashing
  // to empty/spinner when user switches tabs or pulls to refresh.
  const refetch = useCallback(() => {
    if (loadingRef.current) return;
    setPage(1);
    if (called) {
      apolloRefetch(buildVariables(1));
    } else {
      fetchInstances({ variables: buildVariables(1) });
    }
  }, [buildVariables, fetchInstances, called, apolloRefetch]);

  // Use previousData as fallback so the list stays visible while a refetch is in flight.
  // With network-only policy, Apollo clears `data` to undefined before the new response
  // arrives, which would flash the list to empty. previousData holds the last good result.
  const effectiveData =
    data ?? previousData;

  const instances: Instances =
    effectiveData?.multiBuildingInspectionInstances?.inspectionInstances ?? [];

  const pageInfo: PageInfo =
    effectiveData?.multiBuildingInspectionInstances?.pageInfo ?? {
      ...DEFAULT_PAGE_INFO,
      perPage,
    };

  const isInitialLoading = page === 1 && loading && instances.length === 0;

  return {
    instances,
    pageInfo,
    loading,
    isInitialLoading,
    isFetchingMore,
    error,
    page,
    setPage,
    refetch,
  };
}
